import React from 'react';
import { ArrowRight, CheckCircle2, Phone, Users, TrendingUp, MessageSquare, Target, Clock, Zap, ChevronDown, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ApexNavbar from '../components/apex/ApexNavbar';
import ApexFooter from '../components/apex/ApexFooter';

const LeadConversion = () => {
  const navigate = useNavigate();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const goToApplication = () => {
    navigate('/5k-guarantee#application');
  };

  // Done-for-you service features
  const doneForYouFeatures = [
    'Rapid lead contact — we call while the lead is still hot',
    'Consistent follow-up with leads who don\'t respond first time',
    'Re-engagement of suitable older enquiries',
    'Professional sales conversations tailored to your business',
    'Lead qualification and appointment booking',
    'CRM updates and clear reporting on activity and results',
    'A defined process designed to improve lead-to-sale conversion'
  ];

  // Sales coaching features
  const coachingFeatures = [
    'Sales consultation training',
    'How to ask better questions and understand customer needs',
    'Handling objections without sounding pushy',
    'Improving sales scripts and consultation structure',
    'Building confidence when discussing price',
    'Follow-up strategies for undecided prospects',
    'Call reviews, role-play and practical feedback',
    'A repeatable sales process your team can use every day'
  ];

  // Sales journey steps
  const salesJourney = [
    { step: '01', title: 'Lead Generated', description: 'Your marketing attracts the prospect' },
    { step: '02', title: 'Lead Contacted', description: 'Apex starts the conversation' },
    { step: '03', title: 'Follow-Up Completed', description: 'We keep suitable prospects engaged' },
    { step: '04', title: 'Appointment Booked', description: 'Interested prospects are progressed' },
    { step: '05', title: 'Sale Closed', description: 'You close the deal, with Apex coaching available' }
  ];

  // Pain points
  const painPoints = [
    'You\'re on the gym floor running sessions when a new lead comes in — by the time you call back, they\'ve gone cold.',
    'You\'re paying for leads that nobody consistently follows up.',
    'You\'re avoiding follow-up because you don\'t want to feel pushy.',
    'You\'re booking consultations but struggling to convert them.',
    'You\'re too busy running the business to chase every sales opportunity.'
  ];

  // Why Apex
  const whyApex = [
    'Over 10 years of sales experience',
    'Experience across 8,000+ consultations',
    'Practical experience selling fitness programmes and memberships',
    'A process-driven approach to lead management, follow-up and conversion',
    'Done-for-you sales support and coaching under one roof'
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <ApexNavbar />
      
      <main>
        {/* HERO SECTION */}
        <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
          <div className="absolute inset-0 bg-[#0A0A0A]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#22C55E]/5 rounded-full blur-[200px]" />
          
          <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 py-32 md:py-40">
            <div className="text-center space-y-8">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#ffffff15]">
                <span className="w-1.5 h-1.5 bg-[#22C55E] rounded-full" />
                <span className="text-[#71717A] text-xs font-medium tracking-widest uppercase">
                  Lead Conversion Service
                </span>
              </div>

              {/* Main headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight">
                You Generate The Leads.
                <br />
                <span className="text-[#22C55E]">We Turn Them Into Sales.</span>
              </h1>

              {/* Supporting headline */}
              <p className="text-xl md:text-2xl text-white font-medium">
                Your outsourced sales team, without the cost of hiring one.
              </p>

              {/* Supporting copy */}
              <div className="max-w-2xl mx-auto space-y-4">
                <p className="text-lg text-[#71717A] leading-relaxed">
                  <span className="text-white font-semibold">Speed to lead matters.</span> The faster you contact someone, the more likely they are to buy. But you're on the gym floor, running sessions, managing the business — you can't always drop everything to chase a new enquiry.
                </p>
                <p className="text-lg text-[#A1A1AA] leading-relaxed">
                  That's where we come in. The Apex team contacts your leads fast, follows up consistently, and works to convert them into paying customers while you focus on what you do best.
                </p>
                <p className="text-lg text-white font-medium">
                  And if you're struggling to close the sales yourself? We'll coach you and your team to get better at it.
                </p>
              </div>

              {/* Value prop */}
              <p className="text-[#22C55E] font-semibold text-lg">
                More conversations. More conversions. More revenue.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <button
                  onClick={goToApplication}
                  data-testid="hero-cta-primary"
                  className="group inline-flex items-center justify-center gap-2 bg-[#22C55E] hover:bg-[#1DB954] text-black font-semibold rounded-full px-8 py-4 transition-all duration-300"
                >
                  Let's Grow Your Sales
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={() => scrollToSection('how-it-works')}
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center justify-center gap-2 text-[#71717A] hover:text-white font-medium rounded-full px-8 py-4 transition-colors duration-300"
                >
                  How It Works
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#27272A] to-transparent" />
        </section>

        {/* INTRODUCTION SECTION */}
        <section className="relative py-20 md:py-28 bg-[#0A0A0A]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
                Speed To Lead <span className="text-[#22C55E]">Wins Sales.</span>
                <br />
                <span className="text-[#A1A1AA] text-2xl md:text-3xl font-medium">But You're Too Busy To Call Every Lead Instantly.</span>
              </h2>
            </div>

            <div className="space-y-6 text-lg text-[#A1A1AA] leading-relaxed text-center max-w-3xl mx-auto">
              <p>
                Research shows that responding to a lead within 5 minutes makes you <span className="text-white font-semibold">21x more likely</span> to qualify them. Wait an hour? That lead has already moved on.
              </p>
              <p>
                But you're on the gym floor running sessions. You're managing staff. You're dealing with members. You can't drop everything every time a new enquiry comes in.
              </p>
              <p className="text-white font-medium">
                We put a dedicated team behind your leads so they get contacted fast, followed up consistently, and converted into paying customers — while you focus on running your business.
              </p>
            </div>
          </div>
        </section>

        {/* GUARANTEE SECTION */}
        <section className="relative py-20 md:py-28 bg-[#0d0d0d]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="bg-gradient-to-br from-[#121212] to-[#0d1f12] border-2 border-[#22C55E] rounded-3xl p-8 md:p-12">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#22C55E]/20 mb-6">
                  <Target className="w-8 h-8 text-[#22C55E]" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  No Results? <span className="text-[#22C55E]">Money Back.</span>
                </h2>
              </div>

              <div className="space-y-6 text-lg text-[#A1A1AA] leading-relaxed text-center max-w-2xl mx-auto">
                <p>
                  We'll agree on a clear sales target and a fair measurement period based on your business, offer and lead volume before we start.
                </p>
                <p>
                  We'll put the process, follow-up and sales support in place to hit that target.
                </p>
                <p className="text-white font-semibold text-xl">
                  If you don't see results, we'll give you your money back.
                </p>
              </div>

              <div className="mt-8 pt-8 border-t border-[#27272A]">
                <p className="text-sm text-[#71717A] text-center">
                  Specific guarantee terms are agreed based on your business, offer and lead volume.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* TWO SERVICES SECTION */}
        <section id="how-it-works" className="relative py-20 md:py-28 bg-[#0A0A0A]">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Two Ways We Help You{' '}
                <span className="text-[#22C55E]">Grow Sales</span>
              </h2>
              <p className="text-[#71717A] text-lg max-w-2xl mx-auto">
                Use either service independently, or combine both into one complete solution.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Service 1: Done-For-You */}
              <div className="bg-[#121212] border border-[#27272A] rounded-2xl p-8 md:p-10 hover:border-[#22C55E]/30 transition-all duration-300">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#22C55E]/10 flex items-center justify-center">
                    <Phone className="w-6 h-6 text-[#22C55E]" />
                  </div>
                  <span className="text-xs text-[#22C55E] font-semibold tracking-widest uppercase">Service 1</span>
                </div>

                <h3 className="text-2xl font-bold text-white mb-4">
                  Done-For-You Sales
                </h3>

                <p className="text-xl text-[#A1A1AA] font-medium mb-6">
                  We Call. We Follow Up. We Help You Convert.
                </p>

                <p className="text-[#71717A] leading-relaxed mb-8">
                  You're on the gym floor. You're running sessions. You're managing the business. You simply don't have time to call every lead the moment they come in. But speed matters — and every hour you wait, that lead gets colder. Our team contacts your leads fast, handles initial conversations, qualifies interest and books appointments while you do what you do best.
                </p>

                <div className="mb-8">
                  <p className="text-sm text-[#52525B] uppercase tracking-wider mb-4">What's Included:</p>
                  <ul className="space-y-3">
                    {doneForYouFeatures.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#22C55E] mt-0.5 flex-shrink-0" />
                        <span className="text-[#A1A1AA] text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={goToApplication}
                  data-testid="service1-cta"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#22C55E] hover:bg-[#1DB954] text-black font-semibold rounded-full px-6 py-4 transition-all duration-300"
                >
                  Let Apex Handle My Leads
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>

              {/* Service 2: Sales Coaching */}
              <div className="bg-[#121212] border border-[#27272A] rounded-2xl p-8 md:p-10 hover:border-[#22C55E]/30 transition-all duration-300">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#22C55E]/10 flex items-center justify-center">
                    <Users className="w-6 h-6 text-[#22C55E]" />
                  </div>
                  <span className="text-xs text-[#22C55E] font-semibold tracking-widest uppercase">Service 2</span>
                </div>

                <h3 className="text-2xl font-bold text-white mb-4">
                  Sales Coaching
                </h3>

                <p className="text-xl text-[#A1A1AA] font-medium mb-6">
                  We Get You The Opportunities. We'll Help You Close Them.
                </p>

                <p className="text-[#71717A] leading-relaxed mb-8">
                  Sometimes the problem isn't generating leads or booking appointments. It's what happens when you get someone on the phone or sit down for a consultation. If you need help turning interested prospects into paying customers, we'll help you and your team develop the skills and confidence to close more sales.
                </p>

                <div className="mb-8">
                  <p className="text-sm text-[#52525B] uppercase tracking-wider mb-4">What's Included:</p>
                  <ul className="space-y-3">
                    {coachingFeatures.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#22C55E] mt-0.5 flex-shrink-0" />
                        <span className="text-[#A1A1AA] text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={goToApplication}
                  data-testid="service2-cta"
                  className="w-full inline-flex items-center justify-center gap-2 bg-transparent border border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E] hover:text-black font-semibold rounded-full px-6 py-4 transition-all duration-300"
                >
                  Help Me Close More Sales
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* COMBINED OFFER - SALES JOURNEY */}
        <section className="relative py-20 md:py-28 bg-[#0d0d0d]">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                From First Enquiry To Closed Sale.
                <br />
                <span className="text-[#22C55E]">We Can Help At Every Step.</span>
              </h2>
            </div>

            {/* Sales Journey Visual */}
            <div className="relative mb-16">
              {/* Connection line */}
              <div className="hidden md:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-[#22C55E]/20 via-[#22C55E] to-[#22C55E]/20 -translate-y-1/2" />
              
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                {salesJourney.map((item) => (
                  <div key={item.step} className="relative text-center">
                    <div className="bg-[#0A0A0A] relative z-10 p-4">
                      <div className="w-14 h-14 rounded-full bg-[#22C55E] text-black font-bold text-lg flex items-center justify-center mx-auto mb-4">
                        {item.step}
                      </div>
                      <h4 className="text-white font-semibold mb-2">{item.title}</h4>
                      <p className="text-[#71717A] text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="text-center space-y-6 max-w-3xl mx-auto">
              <p className="text-[#A1A1AA] text-lg leading-relaxed">
                The exact division of responsibility depends on the agreed service. Where appropriate, Apex can handle more of the sales process directly rather than simply handing over appointments.
              </p>
              <p className="text-white text-lg font-medium">
                Whether you need someone to take sales calls off your hands, a coach to improve your closing rate, or both, Apex can build a solution around your business.
              </p>
            </div>
          </div>
        </section>

        {/* PAIN POINTS SECTION */}
        <section className="relative py-20 md:py-28 bg-[#0A0A0A]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white">
                How Much Revenue Are You{' '}
                <span className="text-[#22C55E]">Leaving On The Table?</span>
              </h2>
            </div>

            <div className="space-y-4 mb-12">
              {painPoints.map((point) => (
                <div
                  key={point}
                  className="bg-[#121212] border border-[#27272A] rounded-xl p-6 flex items-start gap-4"
                >
                  <AlertCircle className="w-6 h-6 text-[#EF4444] flex-shrink-0 mt-0.5" />
                  <p className="text-white font-medium">{point}</p>
                </div>
              ))}
            </div>

            <div className="text-center">
              <p className="text-xl text-[#22C55E] font-semibold">
                You don't necessarily need more leads. You might need to get more sales from the leads you already have.
              </p>
            </div>
          </div>
        </section>

        {/* WHY APEX SECTION */}
        <section className="relative py-20 md:py-28 bg-[#0d0d0d]">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Sales Is What We Do.
                <br />
                <span className="text-[#22C55E]">Let Us Help You Do More Of It.</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-12">
              {whyApex.map((item) => (
                <div
                  key={item}
                  className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-6 flex items-center gap-4"
                >
                  <CheckCircle2 className="w-6 h-6 text-[#22C55E] flex-shrink-0" />
                  <p className="text-white">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA SECTION */}
        <section className="relative py-20 md:py-28 bg-[#0A0A0A]">
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d1f12]/30 to-transparent" />
          <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Let's Turn More Of Your Leads{' '}
              <span className="text-[#22C55E]">Into Sales.</span>
            </h2>
            <p className="text-lg text-[#A1A1AA] mb-4 max-w-2xl mx-auto">
              Tell us about your business, how your leads are currently handled and where you're struggling to convert them.
            </p>
            <p className="text-lg text-white mb-10 max-w-2xl mx-auto">
              We'll discuss whether you need done-for-you sales support, sales coaching, or a combination of both.
            </p>
            <button
              onClick={goToApplication}
              data-testid="final-cta"
              className="group inline-flex items-center justify-center gap-3 bg-[#22C55E] hover:bg-[#1DB954] text-black font-semibold rounded-full px-10 py-5 text-lg transition-all duration-300"
            >
              Book Your Free Sales Strategy Call
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </section>
      </main>

      <ApexFooter />
    </div>
  );
};

export default LeadConversion;
